const LOGO = 'https://staffhub.wcdns.co.uk/rattlebone-logo.png';

interface CourseInfo {
  displayTitle: string;
  awardLevel: string;
  intro: string;
  topics: string[];
  guidance: string[];
  competence: string;
}

const COURSES: CourseInfo[] = [
  {
    displayTitle: 'Food Hygiene and Food Safety — Level 2',
    awardLevel: 'Level 2 In-House Award',
    intro: 'Comprehensive training in commercial food hygiene and food safety principles, covering the legal obligations, practical procedures, and best practices required to handle food safely in a professional hospitality environment.',
    topics: [
      'Personal hygiene — handwashing, illness, protective clothing',
      'Food contamination: physical, chemical, biological and allergenic',
      'Cross-contamination — causes, risks and prevention procedures',
      'Temperature control — danger zone, cooking temps, chilling and storage',
      'Food storage — FIFO, refrigeration, dry stores, date labelling (use-by vs best-before)',
      'Cleaning and disinfection — schedules, products, procedures and verification',
      'Pest control — recognition, prevention and reporting',
      'HACCP principles — the 7 steps, critical control points and monitoring',
      'Foodborne illness — E. coli, Salmonella, Listeria, Campylobacter: causes, symptoms, prevention',
      'Food safety legislation — Food Safety Act 1990, Food Safety (England) Regulations 2013',
      'Responsibilities of food handlers and food business operators',
    ],
    guidance: [
      'UK Food Standards Agency (FSA) food safety guidance',
      'Food Safety Act 1990',
      'Food Safety and Hygiene (England) Regulations 2013',
      'Regulation (EC) No 852/2004 on the hygiene of foodstuffs',
      'FSA "Safer Food, Better Business" framework',
    ],
    competence: 'has demonstrated sufficient knowledge and understanding of food hygiene and food safety principles to handle food safely in a professional commercial catering environment, and is aware of their legal obligations as a food handler.',
  },
  {
    displayTitle: 'Food Allergen Awareness',
    awardLevel: 'Level 2 In-House Award',
    intro: 'Comprehensive allergen awareness training covering the 14 regulated food allergens, legal requirements under Natasha\'s Law and UK/EU FIC Regulations, safe allergen management, and correct communication with customers about allergen information.',
    topics: [
      'The 14 regulated allergens under UK/EU FIC Regulation and their common sources',
      'Symptoms of allergic reactions — mild, moderate and severe (anaphylaxis)',
      'Difference between food allergy, intolerance and coeliac disease',
      'Cross-contact risks — kitchen management, separate equipment and prep areas',
      'Safe allergen handling and preparation procedures',
      'Menu labelling requirements — mandatory disclosure and precautionary labelling',
      'Natasha\'s Law — pre-packed for direct sale (PPDS) labelling rules',
      'Correct communication with guests about allergen queries',
      'Staff responsibilities and escalation procedures',
      'Emergency response to an allergic reaction — recognising anaphylaxis',
      'Documentation — allergen registers, supplier information and ingredient checks',
    ],
    guidance: [
      'Natasha\'s Law: Food Information (Amendment) (England) Regulations 2019',
      'EU Food Information for Consumers (FIC) Regulation No 1169/2011 (retained in UK law)',
      'UK Food Standards Agency allergen guidance and \'Natasha\'s Law\' toolkit',
      'Allergy UK clinical guidance on major food allergens',
      'HSE guidance on food allergy management in catering',
    ],
    competence: 'has demonstrated knowledge and understanding of food allergen management, legal obligations under Natasha\'s Law and FIC Regulations, and the safe-handling procedures required to manage allergen risks appropriately in a professional hospitality setting.',
  },
  {
    displayTitle: 'Fire Safety Awareness',
    awardLevel: 'In-House Compliance Award',
    intro: 'Essential fire safety training covering fire prevention, emergency procedures, evacuation protocols, and staff responsibilities under UK fire safety law in a hospitality environment.',
    topics: [
      'Fire triangle — heat, fuel and oxygen: causes and prevention',
      'Common fire hazards in hospitality — kitchen, service and back-of-house areas',
      'Fire prevention procedures and daily checks',
      'Fire detection systems — alarms, types and what to do when activated',
      'Emergency evacuation procedures and assembly points',
      'Fire extinguisher types — which to use on which type of fire',
      'Legal responsibilities of staff and employers under the Regulatory Reform Order',
      'Reporting near-misses, hazards and fire safety concerns',
      'Fire risk assessment principles and records',
    ],
    guidance: [
      'Regulatory Reform (Fire Safety) Order 2005',
      'Fire Safety Act 2021',
      'UK Home Office Fire Safety in Premises guidance',
      'HSE Fire Safety at Work guidance',
    ],
    competence: 'has demonstrated awareness of fire safety hazards, prevention procedures, emergency evacuation protocols and their legal responsibilities as a team member in a licensed hospitality environment.',
  },
  {
    displayTitle: 'Health and Safety at Work',
    awardLevel: 'In-House Compliance Award',
    intro: 'Fundamental health and safety awareness training covering legal responsibilities, risk assessment principles, manual handling, workplace hazards, and accident reporting procedures in a hospitality environment.',
    topics: [
      'Health and Safety at Work Act 1974 — employer and employee duties',
      'Risk assessment principles — identify, evaluate, control, review',
      'Manual handling — safe lifting techniques and back injury prevention',
      'Slips, trips and falls — hazards and prevention',
      'Working with chemicals — COSHH regulations and MSDS sheets',
      'Electrical safety — visual checks and reporting defects',
      'Personal Protective Equipment (PPE) — when and how to use it',
      'Accident and near-miss reporting — RIDDOR requirements',
      'First aid — knowing where to find it and when to call',
      'Stress, wellbeing and mental health at work awareness',
    ],
    guidance: [
      'Health and Safety at Work Act 1974',
      'Management of Health and Safety at Work Regulations 1999',
      'Manual Handling Operations Regulations 1992',
      'COSHH Regulations 2002',
      'RIDDOR 2013 (Reporting of Injuries, Diseases and Dangerous Occurrences)',
      'HSE workplace guidance for hospitality and catering',
    ],
    competence: 'has demonstrated awareness of workplace health and safety obligations, risk assessment principles and safe working practices required of a team member in a licensed hospitality environment.',
  },
  {
    displayTitle: 'Licensing Law Compliance',
    awardLevel: 'In-House Compliance Award',
    intro: 'Training in UK licensing law and the responsible sale and supply of alcohol, covering the Licensing Act 2003, the four licensing objectives, legal responsibilities of staff, Challenge 25 and the consequences of licence breaches.',
    topics: [
      'Licensing Act 2003 — four licensing objectives',
      'Conditions of the premises licence and DPS responsibilities',
      'Responsible sale and supply of alcohol',
      'Challenge 25 — application, acceptable ID and refusal procedures',
      'Identifying signs of intoxication and the duty to refuse service',
      'Selling to under-18s — offences and penalties',
      'Proxy sales and the offence of buying alcohol for minors',
      'Permitted hours and extension of hours',
      'Police powers and cooperation with licensing authorities',
      'Record-keeping, CCTV and incident log requirements',
    ],
    guidance: [
      'Licensing Act 2003 and associated Regulations',
      'Home Office Statutory Guidance to Licensing Authorities (s.182)',
      'Portman Group\'s Code of Practice on the Naming, Packaging and Promotion of Alcoholic Drinks',
      'Think 25 / Challenge 25 national guidance',
    ],
    competence: 'has demonstrated understanding of UK licensing law, the responsible sale of alcohol, age verification requirements and the legal responsibilities of a licensed premises employee under the Licensing Act 2003.',
  },
  {
    displayTitle: 'GDPR and Data Protection Awareness',
    awardLevel: 'In-House Compliance Award',
    intro: 'Awareness training on data protection law, covering UK GDPR principles, lawful basis for processing personal data, individual rights, data security and staff responsibilities as data handlers.',
    topics: [
      'UK GDPR and Data Protection Act 2018 — key principles',
      'What constitutes personal data and special category data',
      'Lawful basis for processing personal data',
      'Individuals\' rights: access, erasure, rectification, portability',
      'Data minimisation and storage limitation',
      'Security of personal data — physical, digital and human factors',
      'Data breaches — recognising, reporting and recording',
      'Staff responsibilities as data handlers',
      'Consequences of non-compliance — fines and regulatory action',
    ],
    guidance: [
      'UK General Data Protection Regulation (UK GDPR)',
      'Data Protection Act 2018',
      'ICO guidance on Data Protection Principles',
      'ICO guidance on Personal Data Breaches',
    ],
    competence: 'has demonstrated awareness of UK data protection law, the principles of lawful data processing, and their responsibilities as a data handler in a business environment.',
  },
];

function matchCourse(formType: string, formName: string): CourseInfo {
  const haystack = `${formType} ${formName}`.toLowerCase();
  if (haystack.includes('food hygiene') || haystack.includes('food safety')) return COURSES[0];
  if (haystack.includes('allergen')) return COURSES[1];
  if (haystack.includes('fire')) return COURSES[2];
  if (haystack.includes('health') && haystack.includes('safety')) return COURSES[3];
  if (haystack.includes('licens') || haystack.includes('alcohol') || haystack.includes('dps') || haystack.includes('challenge 25')) return COURSES[4];
  if (haystack.includes('gdpr') || haystack.includes('data protection')) return COURSES[5];
  // Generic fallback
  return {
    displayTitle: formName,
    awardLevel: 'In-House Compliance Award',
    intro: `In-house compliance training covering the key knowledge and responsibilities required of team members in relation to ${formName}.`,
    topics: [
      'Key legislation and regulatory requirements',
      'Responsibilities of team members and management',
      'Safe and compliant working practices',
      'Incident recognition, reporting and escalation',
      'Consequences of non-compliance',
    ],
    guidance: [
      'Relevant UK government guidance and legislation',
      'Rattlebone Sherston Ltd internal policies and procedures',
    ],
    competence: `has demonstrated the required knowledge and awareness in relation to ${formName} and their obligations as a team member of Rattlebone Sherston Ltd.`,
  };
}

function fmtCertDate(iso: string) {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' });
  } catch { return iso; }
}

const SHARED_STYLES = `
  *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }
  html, body { width: 100%; height: 100%; }
  body { font-family: 'Inter', sans-serif; background: #f5f0eb; display: flex; align-items: flex-start; justify-content: center; }
  .page {
    width: 297mm; min-height: 210mm; background: #fff;
    margin: 12mm auto; padding: 0;
    position: relative; overflow: hidden;
  }
  .border-outer { position: absolute; inset: 8mm; border: 3px solid #A36B58; pointer-events: none; z-index: 10; }
  .border-inner { position: absolute; inset: 10.5mm; border: 1px solid #A36B5844; pointer-events: none; z-index: 10; }
  .corner { position: absolute; width: 22px; height: 22px; z-index: 11; }
  .corner-tl { top: 5mm; left: 5mm; border-top: 3px solid #A36B58; border-left: 3px solid #A36B58; }
  .corner-tr { top: 5mm; right: 5mm; border-top: 3px solid #A36B58; border-right: 3px solid #A36B58; }
  .corner-bl { bottom: 5mm; left: 5mm; border-bottom: 3px solid #A36B58; border-left: 3px solid #A36B58; }
  .corner-br { bottom: 5mm; right: 5mm; border-bottom: 3px solid #A36B58; border-right: 3px solid #A36B58; }
  .watermark { position: absolute; inset: 0; background-image: radial-gradient(circle, #A36B5808 1px, transparent 1px); background-size: 18px 18px; z-index: 0; }
  .content { position: relative; z-index: 1; padding: 14mm 18mm 10mm; }
  .header { display: flex; align-items: center; justify-content: space-between; margin-bottom: 4mm; }
  .logo { height: 52px; width: auto; }
  .issuer { font-size: 13px; font-weight: 700; color: #A36B58; letter-spacing: 0.05em; text-transform: uppercase; }
  .issuer-sub { font-size: 10px; color: #888; letter-spacing: 0.06em; text-transform: uppercase; }
  .divider { height: 2px; background: linear-gradient(to right, transparent, #A36B58, transparent); margin: 3mm 0; }
  .thin-divider { height: 1px; background: linear-gradient(to right, transparent, #A36B5844, transparent); margin: 2.5mm 0; }
  .cert-heading { text-align: center; margin: 3mm 0 1.5mm; font-family: 'IM Fell English', serif; font-size: 11px; color: #888; letter-spacing: 0.25em; text-transform: uppercase; }
  .cert-title { text-align: center; margin-bottom: 1mm; font-family: 'IM Fell English', serif; font-size: 28px; color: #A36B58; letter-spacing: 0.06em; }
  .certify-text { text-align: center; font-size: 11px; color: #666; letter-spacing: 0.04em; margin: 3mm 0 1mm; text-transform: uppercase; }
  .staff-name { text-align: center; font-size: 34px; font-weight: 800; color: #1a1a1a; letter-spacing: 0.02em; margin-bottom: 1mm; line-height: 1.1; }
  .staff-role { text-align: center; font-size: 10.5px; color: #888; letter-spacing: 0.05em; text-transform: uppercase; margin-bottom: 2mm; }
  .completed-text { text-align: center; font-size: 11px; color: #555; letter-spacing: 0.02em; margin-bottom: 1mm; }
  .course-title { text-align: center; font-size: 18px; font-weight: 700; color: #A36B58; margin-bottom: 0.5mm; }
  .award-level { text-align: center; font-size: 10px; color: #888; letter-spacing: 0.08em; text-transform: uppercase; }
  .body-cols { display: flex; gap: 6mm; margin-top: 4mm; }
  .col-topics { flex: 2.2; }
  .col-outcome { flex: 1; }
  .col-heading { font-size: 9px; text-transform: uppercase; letter-spacing: 0.1em; color: #A36B58; font-weight: 700; border-bottom: 1.5px solid #A36B5833; padding-bottom: 3px; margin-bottom: 5px; }
  .topics-list { list-style: none; padding: 0; }
  .topics-list li { padding: 3px 0; color: #2d2d2d; font-size: 11.5px; }
  .topics-list li::before { content: '›'; color: #A36B58; font-weight: 700; margin-right: 5px; }
  .outcome-box { background: #faf6f3; border: 1px solid #A36B5822; border-radius: 5px; padding: 8px 10px; margin-bottom: 5px; }
  .outcome-label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #888; margin-bottom: 3px; }
  .outcome-value { font-size: 12px; font-weight: 700; color: #1a1a1a; }
  .pass-badge { display: inline-block; background: #dcfce7; color: #166534; font-weight: 800; font-size: 13px; padding: 2px 10px; border-radius: 3px; letter-spacing: 0.08em; }
  .competence-box { background: linear-gradient(135deg, #faf6f3, #fff); border: 1px solid #A36B5833; border-left: 3px solid #A36B58; border-radius: 4px; padding: 7px 10px; margin-top: 5px; font-size: 10.5px; color: #333; line-height: 1.5; font-style: italic; }
  .sig-row { display: flex; align-items: flex-end; justify-content: space-between; margin-top: 4mm; gap: 6mm; }
  .sig-block { flex: 1; }
  .sig-name { font-family: 'Pinyon Script', cursive; font-size: 32px; color: #1a1a1a; line-height: 1; margin-bottom: 1px; }
  .sig-line { border-top: 1.5px solid #1a1a1a; padding-top: 3px; }
  .sig-title { font-size: 9.5px; color: #555; }
  .sig-org { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #A36B58; font-weight: 600; margin-top: 1px; }
  .date-block { text-align: right; }
  .date-label { font-size: 9px; text-transform: uppercase; letter-spacing: 0.08em; color: #888; }
  .date-value { font-size: 13px; font-weight: 700; color: #1a1a1a; }
  .guidance-row { margin-top: 3mm; padding-top: 2.5mm; border-top: 1px solid #e5e7eb; display: flex; gap: 8mm; }
  .guidance-col { flex: 1.5; }
  .disc-col { flex: 2; }
  .guidance-items { list-style: none; padding: 0; }
  .guidance-items li { font-size: 9px; color: #777; margin-bottom: 2px; }
  .guidance-items li::before { content: '†'; color: #A36B58; margin-right: 4px; }
  .disclaimer { font-size: 8.5px; color: #999; line-height: 1.5; }
  .cert-number { font-size: 8px; color: #bbb; text-align: right; margin-top: 2mm; letter-spacing: 0.05em; }
  @media print {
    body { background: #fff; }
    .page { margin: 0; box-shadow: none; width: 100%; min-height: 100vh; }
    @page { size: A4 landscape; margin: 0; }
  }
`;

const SHARED_HEAD = `
  <meta charset="UTF-8">
  <link rel="preconnect" href="https://fonts.googleapis.com">
  <link href="https://fonts.googleapis.com/css2?family=Pinyon+Script&family=IM+Fell+English:ital@0;1&family=Inter:wght@400;500;600;700;800&display=swap" rel="stylesheet">
`;

const CERT_FRAME = (content: string) => `
  <div class="page">
    <div class="watermark"></div>
    <div class="border-outer"></div>
    <div class="border-inner"></div>
    <div class="corner corner-tl"></div>
    <div class="corner corner-tr"></div>
    <div class="corner corner-bl"></div>
    <div class="corner corner-br"></div>
    <div class="content">${content}</div>
  </div>
`;

const CERT_HEADER = `
  <div class="header">
    <img class="logo" src="${LOGO}" alt="The Rattlebone" />
    <div style="text-align:right;">
      <div class="issuer">Rattlebone Sherston Ltd</div>
      <div class="issuer-sub">Staff Training &amp; Compliance</div>
    </div>
  </div>
  <div class="divider"></div>
`;

export interface CertificateParams {
  staffName: string;
  formName: string;
  formType: string;
  quizScore?: number;
  passMark?: number;
  signedAt: string;
  role?: string;
  department?: string;
}

export function buildComplianceCertificateHtml(p: CertificateParams): string {
  const course = matchCourse(p.formType, p.formName);
  const date = fmtCertDate(p.signedAt);
  const year = new Date().getFullYear();

  const topicsHtml = course.topics.map(t =>
    `<li>${t}</li>`
  ).join('');

  const guidanceHtml = course.guidance.map(g =>
    `<li>${g}</li>`
  ).join('');

  const inner = `
    ${CERT_HEADER}
    <div class="cert-heading">Certificate of Competence</div>
    <div class="certify-text">This is to certify that</div>
    <div class="staff-name">${p.staffName}</div>
    ${p.role || p.department ? `<div class="staff-role">${[p.role, p.department].filter(Boolean).join(' · ')}</div>` : ''}
    <div class="completed-text">has successfully completed training in and passed the assessment for</div>
    <div class="course-title">${course.displayTitle}</div>
    <div class="award-level">${course.awardLevel}</div>
    <div class="thin-divider" style="margin-top:3mm;"></div>
    <div class="body-cols">
      <div class="col-topics">
        <div class="col-heading">Training Topics Covered</div>
        <ul class="topics-list">${topicsHtml}</ul>
      </div>
      <div class="col-outcome">
        <div class="col-heading">Assessment Outcome</div>
        <div class="outcome-box">
          <div class="outcome-label">Written Assessment Score</div>
          <div class="outcome-value">${p.quizScore != null ? `${p.quizScore}%` : 'Confirmed'}</div>
        </div>
        <div class="outcome-box">
          <div class="outcome-label">Pass Mark Required</div>
          <div class="outcome-value">${p.passMark ?? 70}%</div>
        </div>
        <div class="outcome-box">
          <div class="outcome-label">Result</div>
          <div class="pass-badge">PASS ✓</div>
        </div>
        <div class="competence-box">
          <strong style="font-style:normal;font-size:9px;text-transform:uppercase;letter-spacing:0.06em;color:#A36B58;display:block;margin-bottom:3px;">Competence Confirmed</strong>
          ${p.staffName} ${course.competence}
        </div>
      </div>
    </div>
    <div class="sig-row">
      <div class="sig-block">
        <div class="sig-name">Rattlebone</div>
        <div class="sig-line">
          <div class="sig-title">Authorised Signatory — Management</div>
          <div class="sig-org">Rattlebone Sherston Ltd</div>
        </div>
      </div>
      <div class="date-block">
        <div class="date-label">Date of Issue</div>
        <div class="date-value">${date}</div>
      </div>
    </div>
    <div class="guidance-row">
      <div class="guidance-col">
        <div class="col-heading" style="margin-bottom:4px;">Based on Official Guidance</div>
        <ul class="guidance-items">${guidanceHtml}</ul>
      </div>
      <div class="disc-col">
        <div class="col-heading" style="margin-bottom:4px;">Disclaimer</div>
        <p class="disclaimer">
          This compliance certificate confirms that the above-named team member has received in-house training in and been assessed on the subject area shown above.
          All training content, assessment questions and correct answers are based on official United Kingdom government guidance, relevant legislation and regulatory requirements as listed.
          This certificate is issued by Rattlebone Sherston Ltd for internal record-keeping and competence verification purposes only.
          It does not constitute or replace an externally regulated or nationally accredited qualification unless otherwise expressly stated.
        </p>
      </div>
    </div>
    <div class="cert-number">Certificate Ref: RB-${p.formType.toUpperCase().replace(/\s+/g, '-').slice(0,12)}-${p.signedAt ? new Date(p.signedAt).getFullYear() : year} · ${p.staffName.toUpperCase().replace(/\s+/g, '-')}</div>
  `;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  ${SHARED_HEAD}
  <title>Compliance Certificate — ${p.staffName}</title>
  <style>${SHARED_STYLES}</style>
</head>
<body>${CERT_FRAME(inner)}</body>
</html>`;
}

// ─── Module / Training Certificate ───────────────────────────────────────────

export interface ModuleCertificateParams {
  staffName: string;
  moduleTitle: string;
  categoryName: string;
  quizScore: number;
  passMark: number;
  completedDate: string;
  role?: string;
  department?: string;
}

export function buildModuleCertificateHtml(p: ModuleCertificateParams): string {
  const date = fmtCertDate(p.completedDate);
  const year = new Date(p.completedDate).getFullYear();
  const slug = p.moduleTitle.toUpperCase().replace(/\s+/g, '-').slice(0, 16);
  const nameSlug = p.staffName.toUpperCase().replace(/\s+/g, '-');

  const inner = `
    ${CERT_HEADER}
    <div class="cert-heading">Certificate of Achievement</div>
    <div class="certify-text">This is to certify that</div>
    <div class="staff-name">${p.staffName}</div>
    ${p.role || p.department ? `<div class="staff-role">${[p.role, p.department].filter(Boolean).join(' · ')}</div>` : ''}
    <div class="completed-text">has successfully completed the training module and passed the written assessment for</div>
    <div class="course-title">${p.moduleTitle}</div>
    ${p.categoryName ? `<div class="award-level">${p.categoryName} · In-House Training Award</div>` : '<div class="award-level">In-House Training Award</div>'}

    <div class="thin-divider" style="margin-top:4mm;"></div>

    <div class="body-cols">
      <div class="col-topics" style="flex:2.5;">
        <div class="col-heading">About This Training</div>
        <p style="font-size:11px;color:#333;line-height:1.65;margin-bottom:8px;">
          This award recognises that the above-named team member has studied the training materials for <strong>${p.moduleTitle}</strong>${p.categoryName ? ` within the <strong>${p.categoryName}</strong> programme` : ''}, demonstrated understanding through a formal written assessment, and met the required standard for competency.
        </p>
        <p style="font-size:11px;color:#333;line-height:1.65;">
          Training at Rattlebone Sherston Ltd is designed to build practical knowledge and skills relevant to the team member's role, ensuring consistently high standards of service, safety and professionalism across the business.
        </p>
        <div style="margin-top:10px;">
          <div class="col-heading" style="margin-bottom:5px;">Next Steps</div>
          <p style="font-size:10.5px;color:#555;line-height:1.6;">
            Following successful completion of the written assessment, the team member will proceed to a practical sign-off with their line manager to confirm full operational competency and unlock the next stage of their training journey.
          </p>
        </div>
      </div>

      <div class="col-outcome">
        <div class="col-heading">Assessment Result</div>
        <div class="outcome-box">
          <div class="outcome-label">Quiz Score</div>
          <div class="outcome-value" style="font-size:22px;color:#A36B58;">${p.quizScore}%</div>
        </div>
        <div class="outcome-box">
          <div class="outcome-label">Pass Mark</div>
          <div class="outcome-value">${p.passMark}%</div>
        </div>
        <div class="outcome-box">
          <div class="outcome-label">Result</div>
          <div class="pass-badge">PASS ✓</div>
        </div>
        <div class="outcome-box">
          <div class="outcome-label">Status</div>
          <div style="font-size:10px;font-weight:600;color:#1a1a1a;">Awaiting Practical Sign-off</div>
        </div>
        <div class="competence-box" style="margin-top:8px;">
          <strong style="font-style:normal;font-size:9px;text-transform:uppercase;letter-spacing:0.06em;color:#A36B58;display:block;margin-bottom:3px;">Achievement Confirmed</strong>
          ${p.staffName} has passed the written assessment for this module and demonstrated the required level of knowledge.
        </div>
      </div>
    </div>

    <div class="sig-row">
      <div class="sig-block">
        <div class="sig-name">Rattlebone</div>
        <div class="sig-line">
          <div class="sig-title">Training &amp; Development — Rattlebone Sherston Ltd</div>
          <div class="sig-org">Rattlebone Sherston Ltd</div>
        </div>
      </div>
      <div class="date-block">
        <div class="date-label">Date of Issue</div>
        <div class="date-value">${date}</div>
      </div>
    </div>

    <div class="guidance-row">
      <div class="disc-col" style="flex:1;">
        <div class="col-heading" style="margin-bottom:4px;">Note</div>
        <p class="disclaimer">
          This certificate is issued by Rattlebone Sherston Ltd as an in-house training record. It confirms the team member has passed the written knowledge assessment for the module above and does not constitute an externally accredited qualification. Full competency is confirmed following practical sign-off by the team member's line manager.
        </p>
      </div>
    </div>
    <div class="cert-number">Certificate Ref: RB-MOD-${slug}-${year} · ${nameSlug}</div>
  `;

  return `<!DOCTYPE html>
<html lang="en">
<head>
  ${SHARED_HEAD}
  <title>Training Certificate — ${p.staffName}</title>
  <style>${SHARED_STYLES}</style>
</head>
<body>${CERT_FRAME(inner)}</body>
</html>`;
}

// ─── Save as PDF helpers ──────────────────────────────────────────────────────

/**
 * Open the certificate in a new window and immediately trigger the browser's
 * Save as PDF dialog. The certificate renders exactly as-designed — no HTML
 * file is exposed to the user.
 *
 * Browser print dialog tip: choose "Save as PDF" as the destination, set
 * paper size to A4 Landscape, and set margins to None for a pixel-perfect copy.
 */
export function saveCertificateAsPdf(html: string, _filename?: string): void {
  const win = window.open('', '_blank', 'width=1200,height=850');
  if (!win) {
    alert('Please allow pop-ups for this site so your certificate can open.');
    return;
  }

  // Inject a print-on-load script so the dialog fires as soon as fonts load
  const htmlWithAutoprint = html.replace(
    '</body>',
    `<script>
      // Wait for Google Fonts to load before printing so glyphs render correctly
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(function() {
          setTimeout(function() { window.print(); }, 300);
        });
      } else {
        setTimeout(function() { window.print(); }, 1500);
      }
      // Close the tab after the print dialog is dismissed
      window.onafterprint = function() { window.close(); };
    <\/script>
    </body>`
  );

  win.document.open();
  win.document.write(htmlWithAutoprint);
  win.document.close();
}

/**
 * @deprecated Use saveCertificateAsPdf instead — keeps the exact design and
 * prevents users from editing the raw HTML.
 */
export function downloadCertificateFile(html: string, filename: string): void {
  saveCertificateAsPdf(html, filename);
}

/**
 * @deprecated Use saveCertificateAsPdf instead.
 */
export function openCertificateWindow(html: string): void {
  saveCertificateAsPdf(html);
}

/**
 * @deprecated Use saveCertificateAsPdf instead.
 */
export function downloadAndPrintCertificate(html: string, filename: string): void {
  saveCertificateAsPdf(html, filename);
}
